import React, { useState, useMemo } from 'react';
import { IPlace, Button, DateTimeInput, Input, InputContainer, PlaceInput, Maps, fromDateTime, getGMT, Checkbox } from '../../../libs';
import dayjs from 'dayjs';
import localeRu from 'dayjs/locale/ru';
import * as EmailValidator from 'email-validator';
import styled, { css } from 'styled-components';
import { mainApi, s3urls } from '../../../helpers/api';
import { LocationIcon } from '../assets/icons/system';
import { Error } from '../helpers/errors';


interface FormProps {
  token: string;
  partner: string;
  setError(value: Error): void;
}

export default function Form(props: FormProps) {
  const defaultDate = dayjs().toISOString();
  const defaultTime = dayjs().utc().set('hour', 12).set('minute', 0).set('second', 0).toISOString();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [date, setDate] = useState<string>(defaultDate);
  const [time, setTime] = useState<string>(defaultTime);
  const [place, setPlace] = useState<IPlace>({name: '', lat: 0, lon: 0});
  const [dtString, setDtString] = useState<string>('');

  const [showMap, setShowMap] = useState<boolean>(false);

  const [nameMessage, setNameMessage] = useState<string | undefined>();
  const [emailMessage, setEmailMessage] = useState<string | undefined>();
  const [timeMessage, setTimeMessage] = useState<string | undefined>();
  const [dateMessage, setDateMessage] = useState<string | undefined>();
  const [placeMessage, setPlaceMessage] = useState<string | undefined>();
  const [isAgree, setIsAgree] = useState<boolean>(false);
  const [isAgreeMailing, setIsAgreeMailing] = useState<boolean>(false);

  // const [mapUrl, setMapUrl] = useState<string>();

  const isDisabled = useMemo((): boolean => {
    return Boolean(
      nameMessage ||
        !name ||
        emailMessage ||
        !email ||
        timeMessage ||
        !time ||
        dateMessage ||
        !date ||
        placeMessage ||
        !place.name ||
        !isAgree
    );
  }, [nameMessage, name, emailMessage, email, timeMessage, time, dateMessage, date, placeMessage, place, isAgree]);

  const onNameChange = (name: string) => {
    const resultName = name.trim();
    setName(name);
    if (/^[a-z,а-я,ё,\s]*$/i.test(resultName)) {
      setNameMessage(resultName.length === 0 ? 'Имя не может быть пустым' : undefined);
    } else {
      setNameMessage('В имени могут быть только буквы');
    }
  };

  const onEmailChange = (email: string) => {
    setEmail(email);
    setEmailMessage(EmailValidator.validate(email) || email === '' ? undefined : 'Введите корректный email');
  };

  const onDateChange = (isoDate?: string) => {
    const message = 'Нужно ввести дату';
    if (isoDate) {
      setDate(isoDate);
      setDateMessage(
        dayjs(isoDate) < dayjs('1900-01-01')
          ? 'Введите корректную дату'
          : dayjs(isoDate) > dayjs()
            ? 'Введите дату в прошлом'
            : undefined,
      );
    } else {
      setDateMessage(message);
    }
  };

  const onTimeChange = (isoTime?: string) => {
    const message = 'Нужно ввести время';

    if (isoTime) {
      setTime(isoTime);
      setTimeMessage(undefined);
    } else {
      setTimeMessage(message);
    }
  };

  const onPlaceChange = (place: string) => {
    setPlace((place) => ({ ...place, lat: 0, lon: 0 }));
    setPlaceMessage('Выберите город из списка');
  };

  const onPlaceSelect = (place: IPlace | null) => {
    setPlace(place as IPlace);
    setPlaceMessage(undefined);
  }

  const checkErrorMode = (val: any) => (Boolean(val) ? 'error' : 'normal');

  const handleSubmit = () => {
    const formatedDate = dayjs.utc(date).format('DD.MM.YYYY');
    const formatedTime = dayjs.utc(time).format('HH:mm:ss');
    const _dtString = fromDateTime(formatedDate, formatedTime);

    setDate(dayjs(date).locale(localeRu).format('DD MMMM YYYY'));
    setTime(formatedTime);
    setDtString(_dtString);

    setShowMap(true);
  };


  if (showMap) {
    return (
      <Container className='map'>
        <h3>Ваша натальная карта</h3>
        <h5>{date}, {time}, {place.name}</h5>
        <StyledMap
          modes={['natal']}
          form={{
            // @ts-ignore
            natal: {
              // @ts-ignore
              dateTime: {date, time},
              place: place,
              dt: dtString,
            },
          }}
          settings={{
            diameter: 450,
            theme: 'light',
          }}
        />
      </Container>
    );
  }

  return (
    <Container>
      <Description>
        <div className={'title'}>Постройте вашу натальную карту</div>
        <div className={'subtitle'}>Заполните форму и получите вашу карту</div>
      </Description>

      <FormContainer>
        <InputContainer label="Имя" message={nameMessage}>
          <StyledInput
            name="username"
            size="small"
            value={name}
            onChange={onNameChange}
            placeholder="Как Вас зовут?"
            mode={checkErrorMode(nameMessage)}
          />
        </InputContainer>

        <InputContainer label="Почта" message={emailMessage}>
          <StyledInput
            name="email"
            size="small"
            value={email}
            onChange={onEmailChange}
            mode={checkErrorMode(emailMessage)}
            placeholder="Ваш e-mail"
          />
        </InputContainer>

        <InputContainer label="Дата рождения" message={dateMessage}>
          <StyledDateTimeInput
            size="small"
            type="date"
            value={date}
            onChange={onDateChange}
            onInvalid={onDateChange}
            hideIcon={true}
            mode={checkErrorMode(dateMessage)}
            utcMode
          />
        </InputContainer>

        <InputContainer label="Время рождения" message={timeMessage}>
          <StyledDateTimeInput
            size="small"
            type="time"
            value={time}
            hideIcon={true}
            onChange={onTimeChange}
            onInvalid={onTimeChange}
            mode={checkErrorMode(timeMessage)}
            utcMode
          />
        </InputContainer>

        <InputContainer label="Место рождения" message={placeMessage}>
          <StyledPlaceInput
            size="small"
            value={place?.name || ''}
            onSelect={onPlaceSelect}
            onChange={onPlaceChange}
            placeholder="Начните вводить место рождения"
            asyncDataFn={mainApi.places.bind(mainApi)}
            asyncDetailFn={mainApi.place.bind(mainApi)}
            mode={checkErrorMode(placeMessage)}
            lang="ru"
            rightIcon={<LocationIcon/>}
          />
        </InputContainer>

        <StyledButton name="submit-button" color="blue" onClick={handleSubmit} disabled={isDisabled}>
          Построить
        </StyledButton>
      </FormContainer>

      <Footer>
        <div className='row'>
          Нажимая «Построить натальную карту», вы соглашаетесь с <a href="https://minio-api.chronos.mg/public-files/consultations-offer.pdf" target="_blank" rel="noreferrer">офертой</a>
        </div>
        <CheckboxWrapper>
          <StyledCheckbox onChange={setIsAgree} checked={isAgree} color="4093F4" />
          <div>
            Я даю <a href="https://minio-api.chronos.mg/public-files/terms-of-use.pdf" target="_blank" rel="noreferrer">согласие на обработку моих персональных данных</a> в соответствии с <a href="https://minio-api.chronos.mg/public-files/privacy-policy.pdf" target="_blank" rel="noreferrer">Политикой в отношении обработки персональных данных</a>
          </div>
        </CheckboxWrapper>
        <CheckboxWrapper>
          <StyledCheckbox onChange={setIsAgreeMailing} checked={isAgreeMailing} color="4093F4" />
          <div>
            Я принимаю <a href="https://minio-api.chronos.mg/public-files/mailing-agreement.pdf" target="_blank" rel="noreferrer">Согласие на получение рассылки</a>
          </div>
        </CheckboxWrapper>
      </Footer>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  height: 100vh;

  &.map {
    padding: 0 1rem;
  }

  h3 {
    color: var(--text-secondary);
    font-weight: normal;
  }

  h5 {
    margin: 0;
    color: var(--text-third);
    font-weight: normal;
  }

  a {
    text-decoration: none;
    color: #4093f4;
  }

  a:hover {
    text-decoration: underline;
  }

  @media (max-height: 700px) {
    height: 100%;
  }
`;

const Description = styled.div`
  width: 42.5rem;
  max-width: 85%;
  margin-top: 1rem;
  text-align: left;

  .title {
    font-size: 1.25rem;
    font-weight: 500;
    margin-bottom: 0.75rem;
  }

  .subtitle {
    color: var(--text-third);
  }
`;

const FormContainer = styled.div`
  width: 42.5rem;
  max-width: 85%;
  font-size: 0.875rem;
  margin: 2rem 0 1rem;

  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;

  & > :nth-child(5),
  & > :nth-child(n + 6) {
    grid-column: 1 / 3;
  }

  @media (max-width: 768px) {
    & > :nth-child(1) {
      grid-column: 1 / 3;
    }

    & > :nth-child(2) {
      grid-column: 1 / 3;
    }
  }
`;

const StyledInput = styled(Input)<{ error?: boolean }>`
  ${(props) =>
    props.error &&
    css`
      input {
        border-color: var(--red-color);
      }
    `};

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const StyledDateTimeInput = styled(DateTimeInput)<{ error?: boolean }>`
  ${(props) =>
    props.error &&
    css`
      border-color: var(--red-color);
    `};

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const StyledPlaceInput = styled(PlaceInput)<{ error?: boolean }>`
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const StyledButton = styled(Button)`
  color: white !important;
  background: #4093f4 !important;
  font-size: 1.25rem;

  &[disabled] {
    opacity: 0.3;
  }
`;

const Footer = styled.div`
  width: 42.5rem;
  max-width: 85%;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--gray-color);
  font-size: 1rem;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

export const StyledMap = styled(Maps)`
  margin: 5rem 0 5rem;

  @media (max-width: 768px) {
    & {
      width: 83%;
      height: auto;
      /* margin: 3rem auto 4rem; */
    }
      .maps-container > svg {
        width: initial !important;
        height: initial !important;
      }
  }

  svg {
    overflow: visible;
  }

  /* @media print {
    .maps-container > svg {
        width: 450px !important;
        height: 450px !important;
      }
  } */
`;

const CheckboxWrapper = styled.div`
  align-self: flex-start;
  display: inline-flex;
  margin-right: 0.7rem;
  margin-top: 1rem;
  text-align: left;
  gap: 0.3rem;
  font-size: 0.75rem;
`;

const StyledCheckbox = styled(Checkbox)<{ checked: boolean }>`
  width: 1.25rem;
  height: 1.25rem;
  margin-bottom: 0.5rem;
  margin-right: 0.5rem;
  font-size: 14px;
  color: #4093f4;
  border-radius: 0.35rem;
  user-select: 'none';
  border: 1px solid #4093f4 !important;

  & .box {
    align-items: flex-start;
  }

  ${(props) =>
    props.checked &&
    css`
      border: none;
    `}
`;
